/**
 * Unified Content Analyzer
 *
 * Uses Claude to extract structured facets from ANY content type.
 * Source type provides context hints but doesn't restrict facet extraction.
 */

import { callClaude, parseJsonResponse } from '@/lib/ai/claude'
import { useAIStore } from '@/stores/aiStore'
import type {
  AnalysisRequest,
  AnalysisResponse,
  Facet,
  FacetType,
  SourceType,
  SourceScores,
  QualityNote,
  Severity,
  Frequency,
  DecisionType,
  MisalignmentPattern,
  ExtractedEntity,
  ExtractedRelationship,
} from '@/types/interviewer'

// Generate unique IDs
const generateId = () => `facet_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
const generateNoteId = () => `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

// Unified system prompt for KNOWLEDGE GRAPH extraction
// Extracts ENTITIES + RELATIONSHIPS + FACETS for comprehensive analysis
const UNIFIED_SYSTEM_PROMPT = `You are an expert at extracting knowledge graphs from business content.
Your task is to identify:
1. NAMED ENTITIES (specific things mentioned by name or clear description)
2. RELATIONSHIPS between entities (how they connect)
3. SUPPORTING FACETS (evidence and insights)

═══════════════════════════════════════════════════════════════════════════
ENTITY TYPES TO EXTRACT:
═══════════════════════════════════════════════════════════════════════════

MARKET DOMAIN:
• Company - Any company/organization mentioned (including the subject company)
• Industry - Economic sectors or markets (e.g., "SaaS", "FinTech", "Healthcare IT")
• Competitor - Competing companies or products
• CustomerSegment - Target customer groups (e.g., "Mid-market CFOs", "SMB retailers")
• Persona - User archetypes (e.g., "The Overwhelmed Finance Manager")
• Person - Named individuals (e.g., "Sarah, VP of Sales")
• Stakeholder - Groups with decision power (e.g., "IT Security Team")
• Problem - Objective challenges (e.g., "Manual data reconciliation")
• CustomerPain - Emotional/financial impact (e.g., "Wasting 4+ hours weekly")
• CustomerGain - Desired outcomes (e.g., "Save 10 hours/week")
• CustomerJob - Tasks to accomplish (e.g., "Close the books monthly")
• Alternative - Current solutions used (e.g., "Excel spreadsheets")
• Trend - Market directions (e.g., "AI adoption in finance")
• MarketSignal - Indicators of change

BUSINESS DOMAIN:
• ValueProposition - Value offered (e.g., "Automated reconciliation")
• Channel - How to reach customers (e.g., "LinkedIn ads", "Partner network")
• RevenueStream - How money is earned (e.g., "SaaS subscription $99/month")
• KeyResource - Critical assets (e.g., "ML models", "Engineering team")
• KeyActivity - Essential actions (e.g., "Customer onboarding")
• KeyPartnership - Strategic partners (e.g., "AWS", "Salesforce integration")
• CostStructure - Major costs (e.g., "Cloud infrastructure")
• Strategy - Strategic direction
• StrategicGoal - Business objectives
• Decision - Choices made

SOLUTION DOMAIN:
• Product - Products or services (e.g., "Sferio Platform")
• Feature - Product capabilities (e.g., "Auto-reconciliation")
• Requirement - Must-haves
• Constraint - Limitations

═══════════════════════════════════════════════════════════════════════════
RELATIONSHIPS TO DETECT:
═══════════════════════════════════════════════════════════════════════════

Key relationship patterns to look for:
• Company --targetsSegment--> CustomerSegment
• Company --offersProduct--> Product
• Company --operatesInIndustry--> Industry
• Company --competesAgainst--> Competitor
• Company --usesChannel--> Channel
• CustomerSegment --hasPain--> CustomerPain
• CustomerSegment --hasJob--> CustomerJob
• Persona --representsSegment--> CustomerSegment
• Person --belongsToSegment--> CustomerSegment
• Problem --causesPain--> CustomerPain
• Problem --affectsSegment--> CustomerSegment
• ValueProposition --solves--> CustomerPain
• ValueProposition --targets--> CustomerSegment
• Product --addresses--> Problem
• Feature --enables--> CustomerJob
• Alternative --addressesProblem--> Problem
• Trend --impactsIndustry--> Industry
• Channel --reachesSegment--> CustomerSegment

═══════════════════════════════════════════════════════════════════════════
CRITICAL EXTRACTION RULES:
═══════════════════════════════════════════════════════════════════════════

1. EXTRACT NAMED ENTITIES: If a company, person, product, or segment is named, create an entity
   - "Sferio helps mid-market CFOs" → Entity: Company "Sferio", Entity: CustomerSegment "Mid-market CFOs"

2. INFER RELATIONSHIPS: Connect entities based on context
   - "Sferio helps mid-market CFOs" → Relationship: Sferio --targetsSegment--> Mid-market CFOs

3. BE COMPREHENSIVE: Extract ALL entities and relationships, even implicit ones
   - "Our LinkedIn ads reach finance professionals" → Channel "LinkedIn ads", CustomerSegment "Finance professionals", Relationship

4. PRESERVE EVIDENCE: For each entity/relationship, note the supporting text

5. AVOID DUPLICATES: Same entity should be named consistently

Respond in JSON format only.`

// Source-specific context hints
const SOURCE_CONTEXT_HINTS: Record<SourceType, string> = {
  generic: 'This is general text content. Extract all relevant insights.',
  user_interview: `This is a customer/user interview. Pay special attention to:
- Problems they actually experience (not hypotheticals)
- Past behavior and real examples
- Willingness to pay indicators
- Mom Test quality (past behavior vs hypotheticals)`,
  buyer_interview: `This is a buyer/decision-maker interview. Pay special attention to:
- Purchase criteria and decision factors
- Budget and timeline constraints
- Stakeholders involved in decisions
- Competitive alternatives considered`,
  stakeholder_interview: `This is an internal stakeholder interview. Pay special attention to:
- Alignment between teams
- Information gaps
- Action items and ownership
- Dependencies and constraints`,
  document: `This is a document (spec, report, etc). Extract:
- Key decisions and their rationale
- Constraints and dependencies
- Success metrics
- Risks and opportunities`,
  issue: `This is a bug/issue report. Focus on:
- Problem description and severity
- Impact and frequency
- Workarounds mentioned
- Related dependencies`,
  url: `This is web content. Extract:
- Market trends and competitor mentions
- Business context
- Opportunities and threats
- Success metrics if mentioned`,
  meeting_notes: `These are meeting notes. Pay attention to:
- Decisions made and their rationale
- Action items with owners
- Alignment issues
- Dependencies discussed`,
  support_ticket: `This is a support conversation. Focus on:
- Customer problems and their severity
- Current behavior and workarounds
- Frequency indicators
- Willingness to pay signals`,
  customer_interview: `This is a customer interview. Pay special attention to:
- Real problems experienced (not hypotheticals)
- Past behavior and concrete examples
- Value perception and willingness to pay
- Success criteria and expectations`,
  discovery_meeting: `This is a discovery meeting. Focus on:
- Current state and pain points
- Goals and desired outcomes
- Decision-making process
- Timeline and urgency indicators`,
  validation_meeting: `This is a validation meeting. Pay attention to:
- Feedback on proposed solutions
- Concerns and objections raised
- Missing features or capabilities
- Alignment with expectations`,
}

const USER_PROMPT_TEMPLATE = `Extract a KNOWLEDGE GRAPH from this content with entities, relationships, and supporting facets.

SOURCE TYPE: {sourceType}
SOURCE CONTEXT HINT: {sourceHint}

ADDITIONAL CONTEXT: {context}

CONTENT:
{content}

═══════════════════════════════════════════════════════════════════════════
RESPOND WITH THIS EXACT JSON STRUCTURE:
═══════════════════════════════════════════════════════════════════════════
{
  "entities": [
    {
      "id": "<unique_id like company_1, segment_1>",
      "type": "<Company|Industry|Competitor|CustomerSegment|Persona|Person|Stakeholder|Problem|CustomerPain|CustomerGain|CustomerJob|Alternative|Trend|MarketSignal|ValueProposition|Channel|RevenueStream|KeyResource|KeyActivity|KeyPartnership|CostStructure|Strategy|StrategicGoal|Decision|Product|Feature|Requirement|Constraint>",
      "name": "<the actual name or label, e.g. 'Sferio' or 'Mid-market CFOs'>",
      "description": "<brief description>",
      "evidence": "<direct quote from content supporting this entity>",
      "confidence": <0-1>
    }
  ],
  "relationships": [
    {
      "from": "<entity_id>",
      "to": "<entity_id>",
      "type": "<relationship type like targetsSegment, hasPain, offersProduct, operatesInIndustry>",
      "evidence": "<text that supports this relationship>",
      "confidence": <0-1>
    }
  ],
  "facets": [
    {
      "type": "<problem|customer_pain|customer_gain|customer_job|workaround|current_behavior|competitor|alternative|trend|decision|rationale|opportunity|risk|feature_request|requirement|constraint|action_item|quote|observation>",
      "content": "<summary of insight>",
      "evidence": "<direct quote>",
      "confidence": <0-1>,
      "relatedEntityId": "<optional: entity_id this facet relates to>",
      "severity": "<low|medium|high>",
      "frequency": "<rare|occasional|frequent|constant>"
    }
  ],
  "scores": {
    "evidenceStrength": <1-5>,
    "contextRichness": <1-5>,
    "momTestScore": <1-5 if applicable>,
    "rationaleQuality": <1-5 if applicable>,
    "alignmentLevel": <1-5 if applicable>
  },
  "qualityNotes": [
    {
      "type": "<warning|suggestion|success|error>",
      "category": "<category>",
      "message": "<explanation>"
    }
  ],
  "suggestedOntologyLinks": [
    {
      "facetIndex": <index>,
      "entityType": "<suggested ontology entity type>",
      "reason": "<why this maps to ontology>"
    }
  ]
}

Extract ALL entities, relationships, and facets. Be comprehensive - it's better to extract more than miss important ones.
Every item must have supporting evidence from the text.`

interface AnalysisResult {
  entities?: ExtractedEntity[]
  relationships?: ExtractedRelationship[]
  facets: Array<{
    type: FacetType
    content: string
    evidence: string
    confidence: number
    relatedEntityId?: string
    severity?: Severity
    frequency?: Frequency
    decisionType?: DecisionType
    reversibility?: 'one-way-door' | 'two-way-door'
    misalignmentPattern?: MisalignmentPattern
    escalationRisk?: Severity
    evidenceStrength?: number
  }>
  scores: SourceScores
  qualityNotes: Array<{
    type: 'warning' | 'suggestion' | 'success' | 'error'
    category: string
    message: string
  }>
  suggestedOntologyLinks?: Array<{
    facetIndex: number
    entityType: string
    reason: string
  }>
}

/**
 * Analyze content using Claude with unified facet extraction
 */
export async function analyzeContent(
  request: AnalysisRequest
): Promise<AnalysisResponse> {
  const { apiKey, documentAnalysisModel } = useAIStore.getState()

  if (!apiKey) {
    throw new Error('AI not configured. Please add your API key in Settings.')
  }

  const sourceHint = SOURCE_CONTEXT_HINTS[request.sourceType] || SOURCE_CONTEXT_HINTS.generic

  const userPrompt = USER_PROMPT_TEMPLATE
    .replace('{sourceType}', request.sourceType)
    .replace('{sourceHint}', sourceHint)
    .replace('{context}', request.context || 'No additional context provided')
    .replace('{content}', request.content)

  const response = await callClaude(
    {
      apiKey,
      model: documentAnalysisModel,
      maxTokens: 16384, // Increased to match Generic Analyzer for comprehensive extraction
    },
    UNIFIED_SYSTEM_PROMPT,
    userPrompt
  )

  const parsed = parseJsonResponse<AnalysisResult>(response)

  if (!parsed) {
    throw new Error('Failed to parse AI response')
  }

  // Transform to proper Facet objects with IDs
  const facets: Facet[] = parsed.facets.map((f, index) => {
    const suggestedLink = parsed.suggestedOntologyLinks?.find(l => l.facetIndex === index)

    return {
      id: generateId(),
      type: f.type,
      content: f.content,
      evidence: f.evidence,
      confidence: f.confidence,
      severity: f.severity,
      frequency: f.frequency,
      decisionType: f.decisionType,
      reversibility: f.reversibility,
      misalignmentPattern: f.misalignmentPattern,
      escalationRisk: f.escalationRisk,
      evidenceStrength: f.evidenceStrength,
      ontologyLinks: [],
      dismissed: false,
      // Store suggested ontology mapping for UI to present
      suggestedOntologyType: suggestedLink?.entityType,
      suggestedOntologyReason: suggestedLink?.reason,
    }
  })

  const qualityNotes: QualityNote[] = parsed.qualityNotes.map((n) => ({
    id: generateNoteId(),
    type: n.type,
    category: n.category as QualityNote['category'],
    message: n.message,
  }))

  // Extract entities and relationships from the parsed response
  const entities = parsed.entities || []
  const relationships = parsed.relationships || []

  console.log(`[Analyzer] Extracted: ${entities.length} entities, ${relationships.length} relationships, ${facets.length} facets`)

  return {
    facets,
    entities,
    relationships,
    scores: parsed.scores,
    qualityNotes,
  }
}

// Legacy alias for backward compatibility
export const analyzeInterviewContent = analyzeContent

/**
 * Get prompt hints based on source type
 */
export function getSourceTypePromptHints(sourceType: SourceType): string[] {
  switch (sourceType) {
    case 'user_interview':
    case 'buyer_interview':
      return [
        'Tell me about the last time you experienced [problem]',
        'What did you do when that happened?',
        'How much time/money did you spend on that?',
        'Who else has this problem?',
      ]
    case 'stakeholder_interview':
    case 'meeting_notes':
      return [
        'What options did we consider?',
        'Why did we choose this approach?',
        'What constraints affected this decision?',
        'Who owns this?',
      ]
    case 'support_ticket':
      return [
        'How often does this happen?',
        'What\'s the impact when it occurs?',
        'What workarounds have you tried?',
      ]
    case 'issue':
      return [
        'Steps to reproduce',
        'Expected vs actual behavior',
        'Business impact',
        'Related issues or dependencies',
      ]
    default:
      return [
        'What problems or challenges are mentioned?',
        'What decisions were made and why?',
        'Who are the key stakeholders?',
        'What are the risks and opportunities?',
      ]
  }
}

// Legacy alias
export const getModePromptHints = (mode: string) => {
  const modeToSource: Record<string, SourceType> = {
    discovery: 'user_interview',
    decisions: 'meeting_notes',
    alignment: 'stakeholder_interview',
  }
  return getSourceTypePromptHints(modeToSource[mode] || 'generic')
}
